import { Link } from 'react-router';
import { ChevronDown, ArrowRight, Sparkles, Star } from 'lucide-react';
import { Button } from './ui/button';

interface TopLevelItem {
  id: string;
  type: 'link' | 'megamenu' | 'cta';
  label: string;
  link?: string;
  visible: boolean;
  order: number;
}

interface MegaMenuItem {
  id: string;
  type: 'link';
  title: string;
  description?: string;
  link?: string;
  icon?: string;
  featured?: boolean;
  badge?: string;
  tags?: string[];
  visible: boolean;
  order: number;
}

interface FooterSection {
  type: 'none' | 'cta' | 'socialproof' | 'bundle' | 'custom';
  config?: any;
}

interface MegaMenuConfig {
  id: string;
  label: string;
  headerIcon?: string;
  headerTitle: string;
  headerSubtitle?: string;
  headerGradient: string;
  items: MegaMenuItem[];
  footerSection: FooterSection;
}

interface HeaderDynamicNavProps {
  menuItems: TopLevelItem[];
  megaMenus: Record<string, MegaMenuConfig>;
  activeMenu: string | null;
  onMenuEnter: (menuId: string) => void;
  onMenuLeave: () => void;
  onPlannerModalOpen: () => void;
  getIcon: (iconName?: string, size?: string) => JSX.Element;
}

export function HeaderDynamicNav({
  menuItems,
  megaMenus,
  activeMenu,
  onMenuEnter,
  onMenuLeave,
  onPlannerModalOpen,
  getIcon
}: HeaderDynamicNavProps) {
  return (
    <>
      {menuItems
        .filter(item => item.visible)
        .sort((a, b) => a.order - b.order)
        .map(item => {
          // SIMPLE LINK
          if (item.type === 'link') {
            return (
              <Link 
                key={item.id}
                to={item.link || '#'} 
                className="relative px-5 py-2.5 text-gray-700 hover:text-[#D74444] transition-all rounded-xl hover:bg-gradient-to-br hover:from-gray-50 hover:to-gray-100 font-semibold group"
              >
                <span className="relative z-10">{item.label}</span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#D74444]/0 to-[#E87D3E]/0 group-hover:from-[#D74444]/5 group-hover:to-[#E87D3E]/5 rounded-xl transition-all" />
              </Link>
            );
          }
          
          // MEGA MENU
          if (item.type === 'megamenu' && megaMenus[item.id]) {
            const menuData = megaMenus[item.id];
            return (
              <div
                key={item.id}
                className="relative"
                onMouseEnter={() => onMenuEnter(item.id)}
                onMouseLeave={onMenuLeave}
              >
                <button className="relative flex items-center gap-2 px-5 py-2.5 text-gray-700 hover:text-[#D74444] transition-all rounded-xl hover:bg-gradient-to-br hover:from-gray-50 hover:to-gray-100 font-semibold group">
                  <span className="relative z-10">{item.label}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${activeMenu === item.id ? 'rotate-180 text-[#D74444]' : ''}`} />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#2F2E5C]/0 to-[#4a487a]/0 group-hover:from-[#2F2E5C]/5 group-hover:to-[#4a487a]/5 rounded-xl transition-all" />
                </button>
                {activeMenu === item.id && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[480px] animate-in fade-in slide-in-from-top-2 duration-200 z-50">
                    {/* Outer shadow */}
                    <div className="absolute inset-0 bg-black/5 rounded-3xl blur-xl" />
                    
                    <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
                      {/* Header with Gradient */}
                      <div className={`relative ${menuData.headerGradient} px-6 py-5`}>
                        <div className="relative flex items-center gap-3">
                          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center border border-white/30">
                            <div className="text-white">
                              {getIcon(menuData.headerIcon, 'w-6 h-6')}
                            </div>
                          </div>
                          <div>
                            <h3 className="font-bold text-white text-xl">{menuData.headerTitle}</h3>
                            {menuData.headerSubtitle && (
                              <p className="text-sm text-white/90 mt-1">{menuData.headerSubtitle}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-6">
                        {/* Render menu items */}
                        <div className="space-y-4 mb-4">
                          {menuData.items
                            .filter(menuItem => menuItem.visible && menuItem.type === 'link')
                            .sort((a, b) => a.order - b.order)
                            .map((menuItem, index) => {
                              // Check if this item should be in a grid (for compact items)
                              const isCompactItem = menuItem.title === 'Case Studies' || menuItem.title === 'ROI Calculator';
                              const nextItem = menuData.items
                                .filter(mi => mi.visible && mi.type === 'link')
                                .sort((a, b) => a.order - b.order)[index + 1];
                              const isNextCompact = nextItem && (nextItem.title === 'Case Studies' || nextItem.title === 'ROI Calculator');
                              
                              // If this is a compact item and the previous one was already rendered in a pair, skip
                              if (isCompactItem && index > 0) {
                                const prevItem = menuData.items
                                  .filter(mi => mi.visible && mi.type === 'link')
                                  .sort((a, b) => a.order - b.order)[index - 1];
                                const isPrevCompact = prevItem && (prevItem.title === 'Case Studies' || prevItem.title === 'ROI Calculator');
                                if (isPrevCompact) return null;
                              }
                              
                              // Render 2-column grid for compact items
                              if (isCompactItem && isNextCompact) {
                                return (
                                  <div key={menuItem.id} className="grid grid-cols-2 gap-4">
                                    <Link to={menuItem.link || '#'} className="group/item block">
                                      <div className="bg-white hover:bg-gray-50 rounded-2xl p-4 border border-gray-200 hover:border-[#2F2E5C]/20 transition-all h-full">
                                        <div className="w-12 h-12 bg-[#E87D3E] rounded-xl flex items-center justify-center text-white mb-3">
                                          {getIcon(menuItem.icon, 'w-6 h-6')}
                                        </div>
                                        <h4 className="font-bold text-[#2F2E5C] group-hover/item:text-[#D74444] transition-colors mb-1">{menuItem.title}</h4>
                                        {menuItem.description && (
                                          <p className="text-xs text-gray-600">{menuItem.description}</p>
                                        )}
                                      </div>
                                    </Link>
                                    <Link to={nextItem.link || '#'} className="group/item block">
                                      <div className="bg-white hover:bg-gray-50 rounded-2xl p-4 border border-gray-200 hover:border-[#2F2E5C]/20 transition-all h-full">
                                        <div className="w-12 h-12 bg-[#2F2E5C] rounded-xl flex items-center justify-center text-white mb-3">
                                          {getIcon(nextItem.icon, 'w-6 h-6')}
                                        </div>
                                        <h4 className="font-bold text-[#2F2E5C] group-hover/item:text-[#D74444] transition-colors mb-1">{nextItem.title}</h4>
                                        {nextItem.description && (
                                          <p className="text-xs text-gray-600">{nextItem.description}</p>
                                        )}
                                      </div>
                                    </Link>
                                  </div>
                                );
                              }
                              
                              // Regular full-width item
                              if (!isCompactItem || !isNextCompact) {
                                return (
                                  <Link
                                    key={menuItem.id}
                                    to={menuItem.link || '#'}
                                    className="group/item block relative"
                                  >
                                    <div className="bg-white hover:bg-gray-50 rounded-2xl p-5 border border-gray-200 hover:border-[#2F2E5C]/20 transition-all">
                                      {/* Badge */}
                                      {menuItem.badge && (
                                        <div className="absolute top-4 right-4 bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] text-white text-xs font-bold px-3 py-1.5 rounded-lg uppercase">
                                          {menuItem.badge}
                                        </div>
                                      )}
                                      
                                      <div className="flex items-start gap-4">
                                        <div className="flex-shrink-0 w-16 h-16 bg-[#2F2E5C] rounded-2xl flex items-center justify-center text-white">
                                          {getIcon(menuItem.icon, 'w-8 h-8')}
                                        </div>
                                        <div className="flex-1">
                                          <h4 className="font-bold text-lg text-[#2F2E5C] group-hover/item:text-[#D74444] transition-colors mb-1">{menuItem.title}</h4>
                                          {menuItem.description && (
                                            <p className="text-sm text-gray-600 mb-2">{menuItem.description}</p>
                                          )}
                                          {menuItem.tags && menuItem.tags.length > 0 && (
                                            <div className="flex items-center gap-3 text-xs text-gray-500">
                                              {menuItem.tags.map((tag, idx) => (
                                                <span key={idx}>{tag}</span>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </Link>
                                );
                              }
                              
                              return null;
                            })}
                        </div>
                        
                        {/* Footer Section - CTA */}
                        {menuData.footerSection?.type === 'cta' && menuData.footerSection?.config && (
                          <button
                            onClick={() => {
                              if (menuData.footerSection?.config?.action === 'modal') {
                                onPlannerModalOpen();
                              } else if (menuData.footerSection?.config?.link) {
                                window.location.href = menuData.footerSection.config.link;
                              }
                            }}
                            className="w-full bg-gradient-to-r from-[#D74444] via-[#E87D3E] to-[#E8B84D] hover:shadow-lg rounded-2xl p-5 flex items-center gap-4 group/cta transition-all"
                          >
                            <div className="flex-shrink-0 w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                              <div className="text-white">
                                {getIcon(menuData.footerSection.config.icon || 'Download', 'w-7 h-7')}
                              </div>
                            </div>
                            <div className="flex-1 text-left">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-bold text-white text-lg">{menuData.footerSection.config.title}</span>
                                <Sparkles className="w-4 h-4 text-white" />
                              </div>
                              <p className="text-sm text-white/95">{menuData.footerSection.config.description}</p>
                            </div>
                            <ArrowRight className="w-6 h-6 text-white group-hover/cta:translate-x-1 transition-transform flex-shrink-0" />
                          </button>
                        )}
                        
                        {/* Footer Section - Social Proof / Trusted Clients */}
                        {menuData.footerSection?.type === 'socialproof' && menuData.footerSection?.config && (
                          <div className="bg-white rounded-2xl p-5 border border-gray-200">
                            {/* Stars and Rating - Centered */}
                            <div className="flex items-center justify-center gap-2 mb-3">
                              <div className="flex items-center gap-1">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className="w-6 h-6 fill-[#E8B84D] text-[#E8B84D]"
                                  />
                                ))}
                              </div>
                              <span className="text-3xl font-bold text-[#2F2E5C]">
                                {menuData.footerSection.config.rating || '5.0'}
                              </span>
                            </div>
                            
                            {/* Description Text - Centered with client count highlighted */}
                            <p className="text-center text-sm text-gray-600 mb-4">
                              Rated by <span className="font-bold text-[#D74444]">{menuData.footerSection.config.clientCount || '280+'}</span> {menuData.footerSection.config.description || 'happy clients across industries'}
                            </p>
                            
                            {/* Divider */}
                            <div className="border-t border-gray-300 mb-4" />
                            
                            {/* Label - Centered */}
                            <div className="text-center text-xs font-bold text-gray-500 tracking-wider uppercase">
                              {menuData.footerSection.config.label || 'TRUSTED PARTNER'}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          }
          
          // CTA BUTTON
          if (item.type === 'cta') {
            return (
              <Link key={item.id} to={item.link || '#'}>
                <div className="relative group">
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-[#D74444] to-[#E87D3E] rounded-full blur-lg opacity-40 group-hover:opacity-60 transition-opacity" />
                  
                  <Button className="relative bg-gradient-to-r from-[#D74444] to-[#E87D3E] hover:from-[#E87D3E] hover:to-[#E8B84D] text-white rounded-full px-8 h-11 shadow-lg hover:shadow-xl transition-all font-bold group-hover:scale-105">
                    {item.label}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </Link>
            );
          }
          
          return null;
        })}
    </>
  );
}